import {
  Monitor,
  Users,
  GraduationCap,
  Headphones,
  Building2,
  DollarSign,
  Briefcase,
  MapPin,
  CreditCard,
  FileText,
  BarChart,
  Shield,
} from 'lucide-react'

export const categories = [
  { id: 'crms', label: 'CRMs & POS', icon: Monitor, type: 'business' },
  { id: 'recruiting', label: 'Recruiting', icon: Users, type: 'business' },
  { id: 'training', label: 'Training', icon: GraduationCap, type: 'business' },
  {
    id: 'support',
    label: 'Support Services',
    icon: Headphones,
    type: 'business',
  },
  {
    id: 'payroll',
    label: 'Payroll Systems',
    icon: CreditCard,
    type: 'business',
  },
  { id: 'lead-gen', label: 'Lead Gen', icon: BarChart, type: 'business' },
  { id: 'industries', label: 'Industries', icon: Building2, type: 'rep' },
  { id: 'compensation', label: 'Compensation', icon: DollarSign, type: 'rep' },
  { id: 'work-models', label: 'Work Models', icon: Briefcase, type: 'rep' },
  { id: 'territories', label: 'Territories', icon: MapPin, type: 'rep' },
  { id: 'career-paths', label: 'Career Paths', icon: FileText, type: 'rep' },
  {
    id: 'company-reviews',
    label: 'Company Reviews',
    icon: Shield,
    type: 'rep',
  },
]

export const guides = [
  {
    id: 'solar-vs-roofing',
    title: "Solar vs. Roofing: A Rep's Comparison",
    category: 'industries',
    description:
      'We analyze the commission structures, sales cycles, and physical demands of the two biggest D2D verticals.',
    readTime: '8 min read',
    date: 'Oct 12, 2023',
    author: 'D2D Research Team',
    content: `
      <h2>The Great Debate: Solar or Roofing?</h2>
      <p>For years, the door-to-door industry has been dominated by two giants: solar and roofing. Both offer incredible income potential, but the day-to-day reality is vastly different.</p>
      
      <h3>Sales Cycle & Cash Flow</h3>
      <p>Roofing is often an insurance game. You're selling a claim, not just a product. The sales cycle can be shorter, but getting paid depends on insurance adjusters. Solar is a financial product sale. The cycle is longer (install, PTO), but the commissions per deal are typically higher.</p>
      
      <h3>Seasonality</h3>
      <p>Roofing is heavily weather-dependent. You chase storms. Solar is more consistent year-round, though winter months can slow down installs.</p>
      
      <h3>Verdict</h3>
      <p>Choose roofing if you want high volume and shorter sales cycles. Choose solar if you want higher ticket sizes and are willing to manage a longer pipeline.</p>
    `,
  },
  {
    id: 'crm-breakdown',
    title: 'CRM Breakdown: Salesforce vs. HubSpot vs. Industry-Specific',
    category: 'crms',
    description:
      'Which CRM actually works for door-to-door teams? We tested the top 5 contenders on mobile and desktop.',
    readTime: '12 min read',
    date: 'Nov 05, 2023',
    author: 'Tech Evaluation Unit',
    content: `
      <h2>The CRM Landscape</h2>
      <p>Most CRMs are built for desk jockeys, not field reps. We tested the big names against industry-specific tools like SalesRabbit and Spotio.</p>
      
      <h3>Salesforce</h3>
      <p>Powerful but clunky on mobile. Overkill for teams under 50 reps unless you have a dedicated admin.</p>
      
      <h3>HubSpot</h3>
      <p>Great marketing automation, but the map functionality is weak out of the box.</p>
      
      <h3>Industry Specific Tools</h3>
      <p>Tools built for canvassing win on the map interface but often lack the robust reporting of enterprise CRMs. The best stack is often a canvassing app integrated into a major CRM.</p>
    `,
  },
  {
    id: '1099-vs-w2',
    title: 'Understanding 1099 vs. W2 in Door-to-Door',
    category: 'work-models',
    description:
      'The tax implications, legal protections, and income potential of different employment classifications.',
    readTime: '6 min read',
    date: 'Sep 28, 2023',
    author: 'Legal & Compliance Team',
    content: `
      <h2>The Classification War</h2>
      <p>The D2D industry runs on 1099 contractors, but the legal landscape is shifting. Here's what you need to know.</p>
      
      <h3>The 1099 Tradeoff</h3>
      <p><strong>Pros:</strong> Unlimited upside, tax write-offs, autonomy.<br><strong>Cons:</strong> No benefits, pay your own taxes, zero job security.</p>
      
      <h3>The W2 Safety Net</h3>
      <p><strong>Pros:</strong> Benefits, employer pays half of FICA, unemployment insurance.<br><strong>Cons:</strong> Often lower commission caps, stricter schedule control.</p>
    `,
  },
  {
    id: 'recruiting-platforms-review',
    title: 'Top 5 Recruiting Platforms for D2D Teams',
    category: 'recruiting',
    description:
      'We spent $5,000 on ads across 5 platforms to see which one delivered the highest quality candidates.',
    readTime: '10 min read',
    date: 'Dec 15, 2023',
    author: 'Growth Team',
    content: `<h2>Finding Talent is Hard</h2><p>We tested Indeed, ZipRecruiter, LinkedIn, Facebook Ads, and Craigslist. The results surprised us.</p>`,
  },
  {
    id: 'pest-control-comp',
    title: 'Pest Control Compensation Models: 2024 Guide',
    category: 'compensation',
    description:
      'From upfront pay to backend residuals, here is how the top pest control companies structure their offers.',
    readTime: '9 min read',
    date: 'Jan 10, 2024',
    author: 'D2D Research Team',
    content: `<h2>The Golden Handcuffs</h2><p>Pest control is famous for its backend checks. But how long do you have to stay to vest?</p>`,
  },
]
